import { Box, Typography, Chip } from '@mui/material'
import { styled, keyframes } from '@mui/material/styles'
import { TrendingUp, Warning, Star } from '@mui/icons-material'
import { COLORS, TIER_COLORS } from '../../constants/colors'

const fadeIn = keyframes`
  from { opacity: 0; transform: translateX(12px); }
  to   { opacity: 1; transform: translateX(0); }
`

// Map backend signal types → visual variant
function typeVariant(type) {
  if (type === 'HIGH_CONVICTION' || type === 'BUNDLE_DETECTED') return 'boss'
  if (type === 'WHALE_ACCUMULATION') return 'whale'
  if (type === 'RUG_ALERT') return 'rug'
  return 'default'
}

const Card = styled(Box)(({ signalType }) => {
  const v = typeVariant(signalType)
  const border =
    v === 'boss'
      ? COLORS.gold.border
      : v === 'whale'
      ? COLORS.blue.border
      : v === 'rug'
      ? COLORS.red.border
      : COLORS.border.light
  const bg =
    v === 'boss'
      ? COLORS.gold.muted
      : v === 'whale'
      ? COLORS.blue.muted
      : v === 'rug'
      ? COLORS.red.muted
      : COLORS.bg.glass
  return {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '14px 16px',
    borderRadius: 12,
    border: `1px solid ${border}`,
    background: bg,
    animation: `${fadeIn} .35s ease-out`,
    transition: 'background .2s, border-color .2s',
    '&:hover': {
      background: COLORS.bg.glassHover,
      borderColor: typeVariant(signalType) === 'boss' ? COLORS.gold.primary : undefined,
    },
  }
})

const Mono = styled(Typography)(() => ({
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: '0.75rem',
}))

function signalLabel(type) {
  if (type === 'HIGH_CONVICTION') return { text: '👑 High Conviction', color: COLORS.gold.primary }
  if (type === 'BUNDLE_DETECTED') return { text: '🔗 Bundle Detected', color: COLORS.gold.dark }
  if (type === 'WHALE_ACCUMULATION') return { text: '🐋 Whale Entry', color: COLORS.blue.primary }
  if (type === 'RUG_ALERT') return { text: '⚠️ Rug Warning', color: COLORS.red.primary }
  if (type === 'NEW_TOKEN') return { text: '🆕 New Token', color: COLORS.green.primary }
  return { text: '📡 Signal', color: COLORS.green.primary }
}

/**
 * Individual signal card — renders differently based on signalType.
 *
 * @param {{ signal: object }} props
 * signal shape: { id, type, token, symbol, price, change, rugScore, wallet, timestamp, ... }
 */
export default function SignalCard({ signal }) {
  const { type = 'default', token, symbol, price, change, rugScore, wallet, timestamp } = signal
  const label = signalLabel(type)
  const positive = (change ?? 0) >= 0

  return (
    <Card signalType={type}>
      {/* Left icon area */}
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 18,
          flexShrink: 0,
          background: type === 'boss' ? COLORS.gold.muted : COLORS.bg.glass,
          border: `1px solid ${label.color}33`,
        }}
      >
        {typeVariant(type) === 'boss' ? '👑' : typeVariant(type) === 'whale' ? '🐋' : typeVariant(type) === 'rug' ? '⚠️' : type === 'NEW_TOKEN' ? '🆕' : '📡'}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
            {symbol || token || 'Unknown'}
          </Typography>
          <Chip
            label={label.text}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              fontWeight: 700,
              color: label.color,
              background: `${label.color}15`,
              border: `1px solid ${label.color}33`,
            }}
          />
          {rugScore != null && (
            <Chip
              label={`Rug: ${rugScore}`}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 700,
                color: rugScore >= 70 ? COLORS.red.primary : rugScore >= 40 ? COLORS.gold.primary : COLORS.green.primary,
                background: rugScore >= 70 ? COLORS.red.muted : rugScore >= 40 ? COLORS.gold.muted : COLORS.green.muted,
              }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {price != null && <Mono sx={{ color: COLORS.text.secondary }}>${Number(price).toFixed(6)}</Mono>}
          {change != null && (
            <Mono sx={{ color: positive ? COLORS.green.primary : COLORS.red.primary }}>
              {positive ? '+' : ''}{Number(change).toFixed(2)}%
            </Mono>
          )}
          {wallet && (
            <Mono sx={{ color: COLORS.text.muted }}>
              {wallet.slice(0, 4)}…{wallet.slice(-4)}
            </Mono>
          )}
        </Box>
      </Box>

      {/* Timestamp */}
      <Mono sx={{ color: COLORS.text.muted, fontSize: '0.65rem', flexShrink: 0 }}>
        {timestamp ? new Date(timestamp).toLocaleTimeString() : ''}
      </Mono>
    </Card>
  )
}
